<template>
  <div v-if="accountsList && accountsList.length" class="ad-account-selector">
    <div
      v-if="selectedAccount"
      class="ad-account-selector__value"
      @click="showOptions = !showOptions"
    >
      <DepositIcons :service="selectedAccount.service" />
      <span class="ad-account-selector__info">
        <span
          v-if="selectedAccount.description"
          class="ad-account-selector__desc"
          v-html="selectedAccount.description"
        />
        <span class="ad-account-selector__title">
          {{ selectedAccount.account_name }}
        </span>
        <img
          class="ad-account-selector__arrow"
          :class="{ active: showOptions }"
          src="@/assets/icons/icon-arrow.svg"
          alt="arrow"
        />
      </span>
    </div>
    <div v-else class="ad-account-selector__value" @click="showOptions = !showOptions">
      <span class="ad-account-selector__placeholder">
        {{ placeholder }}
      </span>
      <img
        class="ad-account-selector__arrow"
        :class="{ active: showOptions }"
        src="@/assets/icons/icon-arrow.svg"
        alt="arrow"
      />
    </div>
    <div v-show="showOptions" class="ad-account-selector__options">
      <div class="form-input ad-account-selector__search" :class="[{ 'form-input--full': search }]">
        <input v-model="search" type="text" required />
        <span class="form-input__label">
          {{ $t('search_props.placeholder') }}
        </span>
      </div>
      <div
        v-if="showNoAccountOption"
        class="ad-account-selector__item"
        @click="selectAccount(null)"
      >
        <span class="ad-account-selector__title">
          {{ noAccountText }}
        </span>
      </div>
      <div
        v-for="item in filteredAccountsList"
        :key="item.id"
        class="ad-account-selector__item"
        @click="selectAccount(item)"
      >
        <span class="ad-account-selector__service">
          <img :src="getLogo(item.service)" alt="add account logo" />
        </span>
        <span class="ad-account-selector__info">
          <span
            v-if="item.description"
            class="ad-account-selector__desc"
            v-html="item.description"
          />
          <span class="ad-account-selector__title">
            {{ item.account_name }}
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import DepositIcons from '@/components/dialog/DepositIcons.vue';
import { useLogo } from '@/mixin';

export default {
  name: 'AdAccountSelector',
  components: {
    DepositIcons,
  },
  mixins: [useLogo],
  props: {
    accountsList: {
      type: Array,
      default: () => [],
    },
    selectedAccount: {
      type: Object,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    showNoAccountOption: {
      type: Boolean,
      default: false,
    },
    noAccountText: {
      type: String,
      default: '',
    },
  },
  emits: ['update:selectedAccount', 'change'],
  data() {
    return {
      showOptions: false,
      search: '',
    };
  },
  computed: {
    filteredAccountsList() {
      if (!this.search) {
        return this.accountsList;
      }
      const searchLower = this.search.toLowerCase();
      return this.accountsList.filter((account) => {
        return (
          account.account_name.toLowerCase().includes(searchLower) ||
          (account.description && account.description.toLowerCase().includes(searchLower))
        );
      });
    },
  },
  mounted() {
    document.addEventListener(
      'click',
      (e) => {
        if (!e.target.closest('.ad-account-selector')) {
          this.showOptions = false;
        }
      },
      true
    );
  },
  methods: {
    selectAccount(account) {
      this.$emit('update:selectedAccount', account);
      this.$emit('change', account);
      this.showOptions = false;
      this.search = '';
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.ad-account-selector {
  --options-container-max-height: 40rem;
  width: auto;
  position: relative;

  &__value {
    border: 1px solid #d1d4d8;
    border-radius: 2px;
    height: 5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 5rem;
    padding-left: 1.5rem;
    cursor: pointer;
  }

  &__placeholder {
    font-size: 1.6rem;
    line-height: 1.9rem;
    color: #878b93;
  }

  &__arrow {
    position: absolute;
    right: 1rem;
    top: calc(50% - 1rem);
    transition: 0.3s ease-out;
    width: 2rem;
    height: 2rem;

    &.active {
      transform: rotate(-180deg);
    }
  }

  &__options {
    position: absolute;
    border: 1px solid #bfc2c6;
    border-radius: 2px;
    background: #fff;
    z-index: 10;
    width: 100%;
    left: 0;
    bottom: 53px;
    max-height: var(--options-container-max-height);
    overflow-y: auto;
    padding: 0 2rem;
    padding-top: 1rem;
  }

  &__desc {
    font-size: 1.2rem;
    line-height: 1.4rem;
    color: #333333;
  }

  &__title {
    font-size: 1.4rem;
    line-height: 1.6rem;
    color: #7f858d;
    font-weight: 400;
  }

  &__item {
    height: 5.8rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: 0.3s ease-out;
    font-size: 1.4rem;
    border-bottom: 1px solid #edeff3;

    &:last-of-type {
      border-bottom: none;
    }

    &:hover {
      color: var(--primary);
    }
  }

  &__service {
    font-weight: 700;
    & > img {
      display: block;
      max-width: 2rem;
      width: 100%;
    }
  }

  &__info {
    text-align: right;
    display: flex;
    flex-direction: column;
  }

  &__search {
    margin: 10px 0;
  }
}
</style>
